import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { RecordingPlayer } from "../telephony/RecordingPlayer";

// Minimal Voicemails list (WS-3 Phase 3b-1) so nothing is unreachable — matched
// voicemails also show in the lead activity log, but UNMATCHED ones (unknown
// caller → no lead) only surface here. Not a full inbox (that's later): caller +
// time + audio, newest first.

type Row = {
  id: string;
  occurred_at: string;
  duration_sec: number | null;
  recording_url: string | null;
  lead_id: string | null;
  contacts: { first_name?: string; last_name?: string } | null;
};

const fmtDur = (s: number | null) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "—");

const VoicemailsPage = () => {
  const [rows, setRows] = useState<Row[]>([]);
  useEffect(() => {
    getSupabaseClient()
      .from("activities")
      .select("id,occurred_at,duration_sec,recording_url,lead_id,contacts:contact_id(first_name,last_name)")
      .eq("disposition", "voicemail")
      .order("occurred_at", { ascending: false })
      .limit(200)
      .then(({ data }) => setRows((data as unknown as Row[]) ?? []));
  }, []);

  return (
    <div className="p-2 max-w-3xl">
      <h1 className="text-xl font-semibold">Voicemails</h1>
      <div className="text-sm text-muted-foreground mb-4">
        Inbound voicemails, newest first. Matched voicemails also appear in the lead's activity thread.
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Recent voicemails</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {rows.map((r) => {
            const name = r.contacts ? `${r.contacts.first_name ?? ""} ${r.contacts.last_name ?? ""}`.trim() : "";
            return (
              <div key={r.id} className="flex flex-wrap items-center gap-3 border-b pb-3 last:border-0">
                <div className="min-w-40">
                  <div className="text-sm font-medium">{name || "Unknown caller"}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(r.occurred_at).toLocaleString()} · {fmtDur(r.duration_sec)}
                    {!r.lead_id && <Badge variant="outline" className="ml-2 text-[10px]">unmatched</Badge>}
                  </div>
                </div>
                <div className="flex-1 min-w-48">
                  {r.recording_url
                    ? <RecordingPlayer recordingUrl={r.recording_url} />
                    : <span className="text-xs text-muted-foreground">no audio</span>}
                </div>
              </div>
            );
          })}
          {rows.length === 0 && <div className="text-sm text-muted-foreground py-2">No voicemails yet.</div>}
        </CardContent>
      </Card>
    </div>
  );
};

VoicemailsPage.path = "/voicemails";
export default VoicemailsPage;
